<script>
  /**
   * Minimal equity line for one paper brain (coin × TF × period).
   * SVG only — no chart library.
   */
  import { onMount } from 'svelte'
  import { getPaperBalanceSeries } from '../api.js'
  import { fmtNum } from '../lib/format.js'
  import { mskToday } from '../lib/tradingDay.js'

  /** Driven by Overview wallet card selection (coin × TF). */
  export let asset = 'btc'
  export let horizon = '15m'
  /** @type {'day' | '7d'} */
  export let period = 'day'
  /** Optional MSK day for period=day */
  export let day = ''

  let busy = false
  let err = ''
  /** @type {any} */
  let series = null

  $: loadKey = `${asset}|${horizon}|${period}|${day || ''}`
  $: loadKey, scheduleLoad()

  let loadTimer
  function scheduleLoad() {
    clearTimeout(loadTimer)
    loadTimer = setTimeout(load, 0)
  }

  onMount(() => {
    load()
    return () => clearTimeout(loadTimer)
  })

  async function load() {
    busy = true
    err = ''
    try {
      const { res, data, unauthorized } = await getPaperBalanceSeries({
        asset,
        horizon,
        period,
        day: period === 'day' ? day || mskToday() : '',
      })
      if (unauthorized) {
        err = 'нужна авторизация'
        series = null
        return
      }
      if (!res?.ok || !data?.ok) {
        err = (data && data.error) || `ошибка ${res?.status || '?'}`
        series = null
        return
      }
      series = data
    } catch (e) {
      err = String(e && e.message ? e.message : e)
      series = null
    } finally {
      busy = false
    }
  }

  function pickPeriod(p) {
    period = p
  }

  $: points = (series && Array.isArray(series.points) ? series.points : []).filter(
    (p) => p && Number.isFinite(Number(p.equity))
  )
  $: delta = series && series.delta_equity != null ? Number(series.delta_equity) : null
  $: startEq = series && series.start_equity != null ? Number(series.start_equity) : null
  $: endEq = series && series.end_equity != null ? Number(series.end_equity) : null

  const W = 640
  const H = 160
  const PAD_L = 8
  const PAD_R = 8
  const PAD_T = 12
  const PAD_B = 20

  $: pathD = buildPath(points)

  function buildPath(pts) {
    if (!pts || pts.length < 2) return ''
    const xs = pts.map((p) => Number(p.ts))
    const ys = pts.map((p) => Number(p.equity))
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    let minY = Math.min(...ys)
    let maxY = Math.max(...ys)
    if (maxY - minY < 1e-6) {
      minY -= 1
      maxY += 1
    }
    const spanX = Math.max(1, maxX - minX)
    const spanY = maxY - minY
    const innerW = W - PAD_L - PAD_R
    const innerH = H - PAD_T - PAD_B
    return pts
      .map((p, i) => {
        const x = PAD_L + ((Number(p.ts) - minX) / spanX) * innerW
        const y = PAD_T + (1 - (Number(p.equity) - minY) / spanY) * innerH
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(' ')
  }

  $: toneClass =
    delta == null || !Number.isFinite(delta) ? '' : delta > 0 ? 'is-up' : delta < 0 ? 'is-down' : ''
</script>

<section class="bal" aria-labelledby="zone-bal">
  <header class="bal__head">
    <h2 id="zone-bal">Баланс во времени</h2>
    <div class="bal__meta mono">
      {#if busy}
        …
      {:else if series}
        {fmtNum(startEq)} → {fmtNum(endEq)}
        {#if delta != null}
          <em class={toneClass}>{fmtNum(delta, { signed: true })}</em>
        {/if}
      {/if}
    </div>
  </header>

  <div class="bal__controls" role="group" aria-label="Период">
    <div class="bal__seg">
      <button
        type="button"
        class="bal__chip"
        class:is-on={period === 'day'}
        on:click={() => pickPeriod('day')}>день</button
      >
      <button
        type="button"
        class="bal__chip"
        class:is-on={period === '7d'}
        on:click={() => pickPeriod('7d')}>7д</button
      >
    </div>
  </div>

  <div class="bal__chart" aria-busy={busy}>
    {#if err}
      <p class="pm-empty">{err}</p>
    {:else if !points.length && !busy}
      <p class="pm-empty">Нет settle-событий за период — линия появится после ставок.</p>
    {:else if points.length < 2 && !busy}
      <p class="pm-empty">
        Старт {fmtNum(startEq)} · ещё нет движения за
        {period === '7d' ? '7 дней' : 'день'} ({asset.toUpperCase()} · {horizon}).
      </p>
    {:else}
      <svg
        class="bal__svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Equity ${asset} ${horizon}`}
      >
        <path class="bal__line" class:is-up={delta > 0} class:is-down={delta < 0} d={pathD} />
      </svg>
      <p class="bal__hint">
        equity (cash + open) · {period === '7d' ? '7 дней MSK' : 'торговый день MSK'} · {asset
          .toUpperCase()} · {horizon}
      </p>
    {/if}
  </div>
</section>

<style>
  .bal {
    margin: 0;
  }
  .bal__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.65rem;
  }
  .bal__head h2 {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .bal__meta {
    font-size: 0.82rem;
    color: var(--pm-ink-soft);
  }
  .bal__meta em {
    font-style: normal;
    margin-left: 0.35rem;
  }
  .bal__meta em.is-up {
    color: var(--pm-ok);
  }
  .bal__meta em.is-down {
    color: var(--pm-bad);
  }
  .bal__controls {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 0.85rem;
    margin-bottom: 0.75rem;
  }
  .bal__seg {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  .bal__chip {
    appearance: none;
    border: 1px solid var(--pm-line);
    background: transparent;
    color: var(--pm-ink-soft);
    font: inherit;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    padding: 0.28rem 0.55rem;
    border-radius: 0.45rem;
    cursor: pointer;
  }
  .bal__chip:hover {
    color: var(--pm-ink);
    border-color: var(--pm-border-strong);
  }
  .bal__chip.is-on {
    color: var(--pm-ink);
    border-color: var(--pm-ink);
    background: var(--pm-paper-alt);
  }
  .bal__chart {
    min-height: 7.5rem;
  }
  .bal__svg {
    display: block;
    width: 100%;
    height: 9rem;
  }
  .bal__line {
    fill: none;
    stroke: var(--pm-ink);
    stroke-width: 1.6;
    stroke-linejoin: round;
    stroke-linecap: round;
    vector-effect: non-scaling-stroke;
  }
  .bal__line.is-up {
    stroke: var(--pm-ok);
  }
  .bal__line.is-down {
    stroke: var(--pm-bad);
  }
  .bal__hint {
    margin: 0.35rem 0 0;
    font-size: 0.7rem;
    color: var(--pm-ink-mute);
  }
</style>
